/**
 * The router's error screen. A screen whose code is not on this device (an
 * online-only page never opened here, while offline) gets a "needs a
 * connection" state instead of a crash, and retries by itself on reconnect.
 * Field screens never land here: their code is precached (CLAUDE.md §9).
 *
 * Retrying reloads the page: browsers remember a failed module import, so
 * importing it again in the same page fails without trying the network.
 */
import { useEffect, useRef } from 'react';
import { ErrorComponent, type ErrorComponentProps } from '@tanstack/react-router';
import { CloudOff, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/react';
import { isChunkLoadError } from '@/lib/pwa/chunk-error';
import { useSyncStatus } from '@/lib/sync';

export function RouteError(props: ErrorComponentProps) {
  return isChunkLoadError(props.error) ? <NeedsConnection /> : <ErrorComponent {...props} />;
}

function retry() {
  window.location.reload();
}

function NeedsConnection() {
  const t = useT();
  const { connectivity } = useSyncStatus();

  // Back online: load the screen without making the person tap.
  const wasOffline = useRef(false);
  useEffect(() => {
    if (connectivity === 'offline') {
      wasOffline.current = true;
    } else if (wasOffline.current) {
      retry();
    }
  }, [connectivity]);

  return (
    <div
      className="panel flex flex-col items-start gap-3"
      role="alert"
      data-testid="needs-connection"
    >
      <div className="flex items-center gap-2 font-semibold">
        <CloudOff size={18} aria-hidden="true" />
        {t('This page needs a connection')}
      </div>
      <p className="row-detail m-0">
        {t(
          'It has not been opened on this device yet, so it is not saved for offline use. Site pages keep working offline; this one opens once you reconnect.',
        )}
      </p>
      <Button variant="outline" onClick={retry}>
        <RefreshCw />
        {t('Try again')}
      </Button>
    </div>
  );
}
